/**
 * Offboarding Survey Modal (Step 2)
 * 8 questions with select-one or select-up-to-three. Q1 has conditional
 * follow-up text fields. Back returns to acknowledgment; Send submits.
 */
import PropTypes from 'prop-types';
import React from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  TextField
} from '@mui/material';
import { SURVEY_QUESTIONS, AGENCY_INITIATED_SURVEY } from './surveyQuestions';

export default function OffboardingSurveyModal({
  formData,
  setFormData,
  source,
  hideReleaseApprovalQuestion
}) {
  const questions =
    source === 'agencyTable' ? AGENCY_INITIATED_SURVEY : SURVEY_QUESTIONS;

  const handleSelectOne = (questionId, optionId) => {
    const updates = {
      ...formData,
      [questionId]: formData[questionId] === optionId ? '' : optionId
    };
    const question = questions.find(q => q.id === questionId);
    if (question && formData.followUps) {
      const newFollowUps = { ...formData.followUps };
      question.options.forEach(o => {
        if (o.followUp) newFollowUps[`${questionId}_${o.id}`] = '';
      });
      updates.followUps = newFollowUps;
    }
    setFormData(updates);
  };

  const handleSelectUpToThree = (questionId, optionId) => {
    setFormData(() => {
      const current = formData[questionId] || [];
      const isSelected = current.includes(optionId);
      if (isSelected) {
        return {
          ...formData,
          [questionId]: current.filter(id => id !== optionId)
        };
      }
      if (current.length >= 3) return formData;
      return { ...formData, [questionId]: [...current, optionId] };
    });
  };

  const handleOptionChange = (question, option) => {
    if (question.selectType === 'one') {
      handleSelectOne(question.id, option.id);
    } else {
      handleSelectUpToThree(question.id, option.id);
    }
  };

  const isOptionSelected = (question, optionId) => {
    const value = formData[question.id];
    if (question.selectType === 'one') return value === optionId;
    return (value || []).includes(optionId);
  };

  const showFollowUp = (question, optionId) => {
    return formData[question.id] === optionId;
  };

  return (
    <>
      {questions.map((question, index) =>
        hideReleaseApprovalQuestion && question.id === 'q6' ? null : (
          <div key={question.id} style={{ marginBottom: 24 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>
              {index + 1}. {question.question}
            </p>
            <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: 8 }}>
              (
              {question.selectType === 'one'
                ? 'Select one'
                : 'Select up to three'}
              )
            </p>
            <FormGroup>
              {question.options.map(option => (
                <div key={option.id}>
                  <FormControlLabel
                    control={
                      question.selectType === 'one' ? (
                        <Radio
                          checked={isOptionSelected(question, option.id)}
                          onChange={() => handleOptionChange(question, option)}
                        />
                      ) : (
                        <Checkbox
                          checked={isOptionSelected(question, option.id)}
                          onChange={() => handleOptionChange(question, option)}
                        />
                      )
                    }
                    label={option.label}
                  />
                  {option.followUp && showFollowUp(question, option.id) && (
                    <div
                      style={{ marginLeft: 32, marginTop: 8, marginBottom: 8 }}
                    >
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        placeholder={option.followUp.question}
                        value={
                          formData.followUps?.[
                            `${question.id}_${option.id}`
                          ] || ''
                        }
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            followUps: {
                              ...prev.followUps,
                              [`${question.id}_${option.id}`]: e.target.value
                            }
                          }))
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </FormGroup>
          </div>
        )
      )}

      <p style={{ marginTop: 24 }}>
        <b>
          If you have any questions about Offboarding, please reach out to our
          Support team:{' '}
          <span style={{ color: '#3682ac', fontWeight: 'bold' }}>
            support@oneillmarketing.net
          </span>{' '}
          or 813-489-4156.
        </b>
      </p>
    </>
  );
}

OffboardingSurveyModal.propTypes = {
  setFormData: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  source: PropTypes.oneOf(['agencyTable', 'agentInfo']),
  hideReleaseApprovalQuestion: PropTypes.bool
};
